import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import {
  KeywordIdSchema,
  LocationIdSchema,
  LsgReportIdSchema,
  LSG_GRID_SIZES,
  LSG_RUN_STATUS,
  REPORT_SCHEDULE,
  REPORT_SCHEDULE_LAST_DAY_OF_MONTH,
  WEEKDAY_NAMES
} from '@brightlocal/shared';

/** Schema for a keyword in an LSG report. */
const KeywordSchema = z
  .object({
    id: KeywordIdSchema,
    keyword: z.string(),
    search_engine: z.string().optional()
  })
  .transform((data) => camelcaseKeys(data));

/** Schema & type for the settings used to create an LSG report. */
export const LSGCreateReportSettingsSchema = z.object({
  locationId: LocationIdSchema,
  keywords: z.array(z.string()),
  gridSize: z.string(),
  gridPointSpacing: z.string(),
  schedule: z.string()
});
export type LSGCreateReportSettings = z.infer<typeof LSGCreateReportSettingsSchema>;

export const LSGCreateReportResponseSchema = z
  .object({
    report_id: LsgReportIdSchema
  })
  .transform((data) => camelcaseKeys(data));

/** Schema for the response from the API when running an LSG report. */
export const LSGRunReportResponseSchema = z.object({
  message: z.string()
});

export const LSGGridSizeSchema = z.nativeEnum(LSG_GRID_SIZES);
export type LSGGridSize = z.infer<typeof LSGGridSizeSchema>;

/** Schema for an LSG report returned from the API. */
export const LSGReportSchema = z
  .object({
    report_id: LsgReportIdSchema,
    location_id: z.number(),
    business_geo_coordinates: z.object({
      latitude: z.number(),
      longitude: z.number()
    }),
    grid_center_geo_coordinates: z.object({
      latitude: z.number(),
      longitude: z.number()
    }),
    grid_size: LSGGridSizeSchema,
    grid_point_spacing: z.string(),
    keywords: z.array(KeywordSchema),
    report_name: z.string().optional(),
    schedule: z.string(),
    /** Run day of a weekly schedule, capitalised ("Monday"); "None" for other schedules. */
    day_of_week: z.string(),
    day_of_month: z.number().nullable(),
    last_run_id: z.number().nullable(),
    report_urls: z.object({
      report_url: z.string(),
      white_label_url: z.string()
    }),
    credits_per_run: z.number(),
    /** Whether an email goes out when an update finishes. */
    email_alerts_enabled: z.boolean(),
    /** `[""]` when email alerts are off. */
    email_alert_addresses: z.array(z.string()),
    /** Display label of the zone recurring runs start in, e.g. "(UTC-05:00) Eastern Time (US & Canada)"; null when the report has none. */
    time_zone: z.string().nullable(),
    /** Label of the 3-hour window recurring runs start in, e.g. "09:00 AM - 12:00 PM"; null when the report has none. */
    run_time: z.string().nullable(),
    gmb_info: z
      .object({
        id: z.string().optional(),
        cid: z.string().optional(),
        postcode: z.string().optional(),
        telephone: z.string().optional(),
        name: z.string().optional(),
        address: z.string().optional()
      })
      .optional()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type LSGReport = z.infer<typeof LSGReportSchema>;

/** Days of the month a monthly report can run on: 1 to 31, or the last day of the month. */
const LSGDayOfMonthSchema = z.union([z.number().int().min(1).max(31), z.literal(REPORT_SCHEDULE_LAST_DAY_OF_MONTH)]);

/**
 * Partial settings to update a report (camelCase; serialized by `serializeRequestBody`).
 * Omitted fields keep their saved value. Covers keywords, the schedule and email alerts; the other
 * fields the endpoint accepts (location, grid, white label, time zone) are not part of this schema.
 */
export const LSGUpdateReportSettingsSchema = z.object({
  /** The full keyword list that replaces the saved one; at most the plan's keyword limit (30 by default). */
  keywords: z.array(z.string()).min(1).max(30).optional(),
  /** How often the report updates; `adhoc` means only when the customer runs it. */
  schedule: z.enum([REPORT_SCHEDULE.ADHOC, REPORT_SCHEDULE.WEEKLY, REPORT_SCHEDULE.MONTHLY]).optional(),
  /** Run day of a weekly schedule. */
  dayOfWeek: z.nativeEnum(WEEKDAY_NAMES).optional(),
  /** Run day of a monthly schedule; `-1` is the last day of the month. */
  dayOfMonth: LSGDayOfMonthSchema.optional(),
  /** A `run_times` label from `GET /manage/v1/time-options`, e.g. "09:00 AM - 12:00 PM". */
  runTime: z.string().optional(),
  /** Turns the email sent when an update finishes on or off; addresses are required when on. */
  emailAlertsEnabled: z.boolean().optional(),
  /** 1 to 5 addresses when alerts are on; the API refuses an empty list then. */
  emailAlertAddresses: z.array(z.string()).max(5).optional()
});

export type LSGUpdateReportSettings = z.infer<typeof LSGUpdateReportSettingsSchema>;

/** Schema for a single grid point in an LSG report run — carries coordinates and rank for the map. */
export const LSGRunGridPointSchema = z
  .object({
    point_id: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    rank: z.number()
  })
  .transform((data) => camelcaseKeys(data));

export type LSGRunGridPoint = z.infer<typeof LSGRunGridPointSchema>;

/** Schema for a single LSG report run — shared by both the runs list and the latest-run endpoint. */
export const LSGReportRunSchema = z
  .object({
    run_id: z.number(),
    status: z.nativeEnum(LSG_RUN_STATUS),
    summary: z.object({
      avg_rank: z.number(),
      num_high_ranking_points: z.number(),
      num_points: z.number()
    }),
    start_date: z.string(),
    end_date: z.string(),
    grid_points: z.array(LSGRunGridPointSchema).optional().default([])
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type LSGReportRun = z.infer<typeof LSGReportRunSchema>;

export const LSGReportRunsResponseSchema = z.object({
  items: z.array(LSGReportRunSchema)
});

export type LSGReportRuns = z.infer<typeof LSGReportRunsResponseSchema>;

/** Schema for a competitor in an LSG report run. */
const LSGCompetitorSchema = z
  .object({
    cid: z.string(),
    place_id: z.string().optional(),
    title: z.string(),
    avg_rank: z.number(),
    rank: z.number().optional(),
    review_rating: z.number(),
    num_reviews: z.number(),
    links: z.number(),
    authority: z.number(),
    primary_category: z.string().nullable(),
    additional_categories: z.array(z.string()),
    website: z.string(),
    profile_url: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type LSGCompetitor = z.infer<typeof LSGCompetitorSchema>;

/** Schema for the response from the API when fetching competitors for an LSG report run. */
export const LSGCompetitorsResponseSchema = z
  .object({
    run_id: z.number(),
    status: z.string(),
    keyword: z.string(),
    start_date: z.string(),
    end_date: z.string(),
    competitors: z.array(LSGCompetitorSchema)
  })
  .transform((data) => camelcaseKeys(data));

export type LSGCompetitorsResponse = z.infer<typeof LSGCompetitorsResponseSchema>;

/** Schema for a single business ranked at an LSG grid point. */
const LSGGridPointSchema = z
  .object({
    rank: z.number(),
    name: z.string(),
    is_customer_business: z.boolean(),
    num_reviews: z.number(),
    review_rating: z.number(),
    profile_url: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type LSGGridPoint = z.infer<typeof LSGGridPointSchema>;

/** Schema for a single grid point in a competitor's ranking across the grid. No point_id — the API omits it for competitor points. */
const LSGCompetitorGridPointSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  rank: z.number()
});

export type LSGCompetitorGridPoint = z.infer<typeof LSGCompetitorGridPointSchema>;

/** Schema for the response from the API when fetching grid rankings for a specific competitor. */
export const LSGCompetitorRankingsSchema = z
  .object({
    run_id: z.number(),
    status: z.string(),
    keyword: z.string(),
    start_date: z.string(),
    end_date: z.string(),
    summary: z.object({
      avg_rank: z.number(),
      num_points: z.number()
    }),
    grid_points: z.array(LSGCompetitorGridPointSchema).optional().default([])
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type LSGCompetitorRankings = z.infer<typeof LSGCompetitorRankingsSchema>;

/**
 * Schema for the response from the API when fetching the leaderboard for a single LSG grid point.
 * Powers the "Top N for {keyword}" hover/click tooltip on the rankings grid.
 */
export const LSGGridPointsResponseSchema = z.object({
  items: z.array(LSGGridPointSchema)
});

export type LSGGridPointsResponse = z.infer<typeof LSGGridPointsResponseSchema>;
